// Take playback for TTSView: chip plays, Play-All-Favorites, and the
// pipeline state (mode, highlighted blocks, cancel flag, in-flight fetch
// controllers) that the Generate-All pipeline in useTtsGeneration drives.
//
// Owns the playback-mutex registrations ('take', 'pipeline') and the
// cross-tree cancel hook (pipelineCoordinator). Effect order is the same as
// when this lived inline in TTSView: unmount-stop, 'take' stop handler,
// unmount-cancel, coordinator, 'pipeline' stop handler.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Block as BlockT, Take } from '@shared/types';
import { useSessionStore } from '@/stores/session';
import type { useToast } from '@/components/ToastProvider';
import { useVideoDucking } from '@/hooks/useVideoDucking';
import { playAudio, type PlayHandle } from '@/lib/audioPlayback';
import { onCancelPipeline, requestStopPreview } from '@/lib/pipelineCoordinator';
import { clearPlayback, registerPlaybackStop, startPlayback } from '@/stores/playback';

export type PipelineMode = 'idle' | 'generate-all' | 'play-all';

interface UseTakePlaybackOptions {
  blocks: BlockT[];
  currentAssetIdx: number;
  toast: ReturnType<typeof useToast>;
}

export function useTakePlayback({ blocks, currentAssetIdx, toast }: UseTakePlaybackOptions) {
  // `playing` tracks which take is currently playing, used to highlight the
  // chip with .playing. `playerRef` holds the unified audio PlayHandle so
  // Stop-on-new-play and toggle-pause work without coupling to the DOM tree.
  const [playing, setPlaying] = useState<{ blockIdx: number; takeIdx: number } | null>(null);
  const playerRef = useRef<PlayHandle | null>(null);
  /** Monotonic counter incremented on every play start / stop. Lets a
   *  late-arriving decoded handle (the play() promise resolves after the
   *  user has clicked another chip) detect that it's stale and stop()
   *  itself before clobbering the active playback. */
  const playEpochRef = useRef(0);

  // Auto-duck the active <video> while a voice take is playing.
  const { duck, unduck } = useVideoDucking();

  // ── Pipeline (Generate All / Play All Favorites) ─────────────────────
  //
  // A single pipeline runs at a time. `pipelineMode` tracks which one is
  // active. Generate-All uses the legacy two-pipeline pattern (vanilla
  // `generateAllBlocks` ~5290): a GEN loop and a PLAY loop run in
  // parallel — gen-(N+1)'s HTTP request kicks off as soon as gen-N's
  // returns, while play-N is still autoplaying audibly. `pipelineGenIdx`
  // tracks the gen loop's current block; `pipelineBlockIdx` tracks the
  // play loop's. A ref-backed cancel flag + a ref-backed audio-ended
  // resolver implement the legacy `_playPipelineResolve` deadlock-fix
  // pattern: when cancel fires we call the resolver to unblock the
  // awaited `ended` promise immediately instead of waiting for the
  // current take to finish playing.
  const [pipelineMode, setPipelineMode] = useState<PipelineMode>('idle');
  // Block currently being PLAYED by the pipeline (yellow-arrow highlight).
  // `-1` when nothing is playing. Only ever one at a time — the play loop
  // is sequential.
  const [pipelineBlockIdx, setPipelineBlockIdx] = useState<number>(-1);
  // Block currently being GENERATED by the Generate-All pipeline. Vanilla
  // ran the gen loop sequentially in parallel with the play loop, so this
  // is also a single index, but it's tracked separately from
  // `pipelineBlockIdx` so the user SEES gen advancing one block ahead of
  // playback (`-1` when no gen is in-flight).
  const [pipelineGenIdx, setPipelineGenIdx] = useState<number>(-1);
  const pipelineCancelRef = useRef<boolean>(false);
  const pipelinePlayerRef = useRef<PlayHandle | null>(null);
  const pipelineResolveRef = useRef<(() => void) | null>(null);

  // In-flight `fetch` controllers for both single-block Generate and the
  // Generate-All pipeline. Tracked in a Set so the asset-switch effect can
  // abort every outstanding request — the user expects switching assets to
  // stop ALL background work for the previous asset (not just whatever is
  // visible). A single shared Set keeps the bookkeeping uniform; each
  // generation registers its controller on enter and removes it on exit.
  const inflightControllersRef = useRef<Set<AbortController>>(new Set());

  const stopPlayback = useCallback(() => {
    // Bump the epoch first so any in-flight play() promise resolving
    // after this point sees itself as stale and self-stops.
    playEpochRef.current += 1;
    const h = playerRef.current;
    if (h) {
      try { h.stop(); } catch { /* ignore */ }
      playerRef.current = null;
    }
    setPlaying(null);
    // Mutex bookkeeping — guarded clear so we only release the slot if it
    // is still ours (the new playback's startPlayback() will already have
    // overwritten it when it called us).
    clearPlayback('take');
  }, []);

  // Stop playback on unmount so navigation away doesn't leave an orphan
  // <audio> running (legacy "Audio Stop bei Navigation" pattern).
  useEffect(() => () => stopPlayback(), [stopPlayback]);

  // Register the take-playback stop handler with the global playback mutex.
  // When Timeline or a pipeline starts, the mutex calls this so the chip
  // audio goes silent before the new source is audible.
  useEffect(() => registerPlaybackStop('take', stopPlayback), [stopPlayback]);

  /** Cancel any in-flight pipeline. Stops current audio, flips the cancel
   *  flag so subsequent loop iterations short-circuit, resolves the
   *  awaited `ended` promise so the play loop wakes up immediately, AND
   *  aborts every in-flight TTS fetch so the gen pipeline (which now runs
   *  concurrently with playback in the legacy two-pipeline pattern) stops
   *  hitting ElevenLabs. Without the abort, clicking Cancel mid-gen would
   *  leave the network request running and only stop on its next
   *  iteration — defeats the user's intent and wastes a credit. Safe to
   *  call when no pipeline is running — it's a no-op in that case. */
  const cancelPipeline = useCallback(() => {
    pipelineCancelRef.current = true;
    const h = pipelinePlayerRef.current;
    if (h) {
      try { h.stop(); } catch { /* ignore */ }
      pipelinePlayerRef.current = null;
    }
    if (pipelineResolveRef.current) {
      const r = pipelineResolveRef.current;
      pipelineResolveRef.current = null;
      r();
    }
    // Abort every in-flight TTS request. The single-block handleGenerate
    // path also goes through `inflightControllersRef`; that's fine
    // because cancelPipeline is only invoked when a pipeline is active,
    // and pressing "Generate All" already cancels any single-block gen.
    for (const c of inflightControllersRef.current) {
      try { c.abort(); } catch { /* ignore */ }
    }
  }, []);

  // Cancel pipelines on unmount so navigating away mid-pipeline doesn't
  // leave background promises racing with a fresh mount.
  useEffect(() => () => { cancelPipeline(); }, [cancelPipeline]);

  // Register cancelPipeline with the coordinator so Timeline-preview
  // starts can abort any running Generate-All / Play-All without
  // threading a callback through the component tree.
  useEffect(() => onCancelPipeline(cancelPipeline), [cancelPipeline]);

  // Register the pipeline stop handler with the global playback mutex
  // (Generate-All + Play-All-Favorites both use this). When any other
  // playback kind starts, the mutex aborts the running pipeline.
  useEffect(
    () => registerPlaybackStop('pipeline', cancelPipeline),
    [cancelPipeline],
  );

  /** Await the end of a take's playback, resolving immediately if
   *  cancelPipeline() fires. Ported from legacy _playPipelineResolve
   *  pattern. Routes through the unified Web Audio voice bus so mixer
   *  fader/mute apply automatically; ducks/unducks the active <video>
   *  around playback. */
  const playAndWait = (take: Take): Promise<void> => {
    return new Promise<void>((resolve) => {
      if (pipelineCancelRef.current) { resolve(); return; }
      pipelineResolveRef.current = resolve;
      const done = () => {
        if (pipelineResolveRef.current === resolve) {
          pipelineResolveRef.current = null;
        }
        if (pipelinePlayerRef.current) {
          pipelinePlayerRef.current = null;
        }
        unduck();
        resolve();
      };
      duck();
      void playAudio({
        source: take.buffer,
        cacheKey: take.id,
        routing: 'voice-bus',
        bypassMixer: true,
        onEnded: done,
      })
        .then((handle) => {
          // Late-arriving handle for a cancelled pipeline — stop it before
          // assigning so the audio doesn't bleed past the cancel.
          if (pipelineCancelRef.current) {
            try { handle.stop(); } catch { /* ignore */ }
            done();
            return;
          }
          pipelinePlayerRef.current = handle;
        })
        .catch(() => {
          // Autoplay block / decode failure — unduck and fall through so
          // the pipeline doesn't stall on a silent take with a permanently-
          // ducked video.
          done();
        });
    });
  };

  const handlePlayAllFavorites = async () => {
    if (pipelineMode !== 'idle') {
      cancelPipeline();
      return;
    }
    const snapshot =
      useSessionStore.getState().assetBlocks[currentAssetIdx] ??
      useSessionStore.getState().blocks;
    const favs: Array<{ idx: number; take: Take }> = [];
    snapshot.forEach((b, i) => {
      const fav = b.takes[b.favIdx];
      if (b.favIdx >= 0 && fav) {
        favs.push({ idx: i, take: fav });
      }
    });
    if (favs.length === 0) {
      toast.show('No favorites to play.', 2500);
      return;
    }
    // Stop any Timeline-preview before we chain take playback.
    requestStopPreview();
    stopPlayback();
    startPlayback('pipeline');
    pipelineCancelRef.current = false;
    setPipelineMode('play-all');
    let cancelled = false;

    try {
      for (const { idx, take } of favs) {
        if (pipelineCancelRef.current) { cancelled = true; break; }
        setPipelineBlockIdx(idx);
        await playAndWait(take);
        if (pipelineCancelRef.current) { cancelled = true; break; }
      }
    } finally {
      setPipelineMode('idle');
      setPipelineBlockIdx(-1);
      pipelinePlayerRef.current = null;
      pipelineResolveRef.current = null;
      pipelineCancelRef.current = false;
      clearPlayback('pipeline');
    }

    if (cancelled) toast.show('Play All cancelled', 2500);
    else toast.show('Play All complete', 2500);
  };

  const handleChipPlay = (blockIdx: number, takeIdx: number) => {
    const take = blocks[blockIdx]?.takes[takeIdx];
    if (!take) return;
    const isCurrent =
      playing && playing.blockIdx === blockIdx && playing.takeIdx === takeIdx;
    if (isCurrent) {
      stopPlayback();
      unduck();
      return;
    }
    // A chip click is an explicit "play this only" intent — cancel any
    // running pipeline (Generate-All / Play-All-Favorites) and stop the
    // timeline preview before starting the chip's own playback.
    if (pipelineMode !== 'idle') cancelPipeline();
    requestStopPreview();
    stopPlayback();
    // Claim the playback mutex AFTER stopPlayback() above (which would
    // clearPlayback('take') if anything was running) so we hold the slot
    // for the audio we are about to start.
    startPlayback('take');
    setPlaying({ blockIdx, takeIdx });
    duck();
    // Latch the per-click epoch so a late-arriving decoded handle for a
    // play that the user has since superseded knows to abort instead of
    // clobbering the active play.
    const epoch = ++playEpochRef.current;
    let assignedHandle: PlayHandle | null = null;
    const clearIfThisHandle = () => {
      if (playerRef.current === assignedHandle) {
        playerRef.current = null;
        setPlaying(null);
        clearPlayback('take');
      }
      unduck();
    };
    // Unified Web Audio path: decoded buffer is cached by take.id, so a
    // second click on the same chip is sample-accurate (no re-decode, no
    // Safari HTMLAudio buffer-and-go latency).
    void playAudio({
      source: take.buffer,
      cacheKey: take.id,
      routing: 'voice-bus',
      bypassMixer: true,
      onEnded: clearIfThisHandle,
    })
      .then((handle) => {
        // Stale guard: epoch was bumped by another click / stopPlayback.
        if (playEpochRef.current !== epoch) {
          try { handle.stop(); } catch { /* ignore */ }
          return;
        }
        assignedHandle = handle;
        playerRef.current = handle;
      })
      .catch(() => {
        // Autoplay blocked / decode failure — roll back duck + state.
        clearIfThisHandle();
      });
  };

  return {
    playing,
    setPlaying,
    playerRef,
    playEpochRef,
    duck,
    unduck,
    stopPlayback,
    pipelineMode,
    setPipelineMode,
    pipelineBlockIdx,
    setPipelineBlockIdx,
    pipelineGenIdx,
    setPipelineGenIdx,
    pipelineCancelRef,
    pipelinePlayerRef,
    pipelineResolveRef,
    inflightControllersRef,
    cancelPipeline,
    playAndWait,
    handlePlayAllFavorites,
    handleChipPlay,
  };
}
